/**
 * Single fetch wrapper for the Kidology SPA.
 *
 * - `credentials: "include"` so the kidology_session cookie travels on every API request.
 * - `X-Requested-With: XMLHttpRequest` is required by plan 01-06's CsrfHeaderMiddleware on
 *   all non-GET /api/* requests (CONTEXT D-02). We add it on every request unconditionally.
 * - Throws `ApiError` on non-2xx so callers can branch on `error.status`.
 */

const API_BASE = (import.meta as ImportMeta).env?.VITE_API_BASE ?? "";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message?: string
  ) {
    super(message ?? `HTTP ${status}`);
    this.name = "ApiError";
  }
}

/**
 * Extracts a human-readable message from an error thrown by `api()`. Server
 * error envelopes follow the `AdminErrorResponse` shape `{ error, details? }`;
 * for upstream-provider errors (e.g. Mux 502) the actionable text lives in
 * `details.message`. Falls back to `err.message` then the supplied fallback.
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === "object") {
    const body = err.body as { details?: unknown; error?: unknown };
    if (body.details && typeof body.details === "object") {
      const details = body.details as { message?: unknown };
      if (typeof details.message === "string" && details.message.length > 0) {
        return details.message;
      }
    }
    if (typeof body.error === "string" && body.error.length > 0) {
      return body.error;
    }
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const headers = new Headers(init.headers);
  if (!headers.has("Content-Type") && init.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }
  headers.set("X-Requested-With", "XMLHttpRequest");

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers,
  });

  if (!res.ok) {
    let body: unknown;
    const contentType = res.headers.get("content-type") ?? "";
    try {
      body = contentType.includes("application/json")
        ? await res.json()
        : await res.text();
    } catch {
      body = null;
    }
    throw new ApiError(res.status, body);
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

/* ──────────────────────────────────────────────────────────────────────
 * Admin API surface (Slice B / plan 03-02).
 *
 * Snake_case wire format mirrors the public catalog convention (Phase 2).
 * `price_cents` is intentionally absent from admin DTOs — courses are free
 * in v1; the column stays in the DB schema but admin tooling never edits it.
 * ────────────────────────────────────────────────────────────────────── */

export type AdminCourseStatus = "draft" | "published" | "unpublished";

export type AdminCourseSummary = {
  id: string;
  slug: string;
  title: string;
  subtitle: string;
  status: AdminCourseStatus;
  // Quick task 260512-mzr (v1_fixes #6): age_group string replaced by numeric
  // [age_from, age_to] integer range.
  age_from: number;
  age_to: number;
  themes: string[];
  // Quick task 260510-uae: hero_palette + hero_pattern replaced by a nullable
  // image-key + a server-resolved public URL. The s3_key is what the wizard
  // form persists post-upload; the URL is what EditorOverviewPanel renders.
  hero_image_s3_key: string | null;
  hero_image_url: string | null;
  hero_label_top: string;
  hero_label_main: string;
  hero_label_subtitle: string;
  display_order: number;
  created_at: string;
  updated_at: string;
  // Quick task 260512-ncm (v1_fixes #8): per-course curriculum-section label
  // (e.g. "Day", "Week", "Module"). Server-side default is "Day"; admin
  // Overview panel exposes a text input to override per-course. Flows into
  // AdminCourseDetail + UpdateCourseRequest transitively.
  day_label: string;
  spanish_title?: string | null;
  spanish_subtitle?: string | null;
  spanish_hero_label_top?: string | null;
  spanish_hero_label_main?: string | null;
  spanish_hero_label_subtitle?: string | null;
  spanish_day_label?: string | null;
  spanish_hero_image_s3_key?: string | null;
  spanish_hero_image_url?: string | null;
  portuguese_title?: string | null;
  portuguese_subtitle?: string | null;
  portuguese_hero_label_top?: string | null;
  portuguese_hero_label_main?: string | null;
  portuguese_hero_label_subtitle?: string | null;
  portuguese_day_label?: string | null;
  portuguese_hero_image_s3_key?: string | null;
  portuguese_hero_image_url?: string | null;
};

export type AdminCourseDetail = AdminCourseSummary & {
  short_description: string;
  long_description: string;
  course_includes: string[];
  spanish_short_description?: string | null;
  spanish_long_description?: string | null;
  spanish_course_includes?: string[];
  portuguese_short_description?: string | null;
  portuguese_long_description?: string | null;
  portuguese_course_includes?: string[];
};

// Quick task 260514-omd — server-side offset/limit pagination. Each admin
// list response carries the items-array key (`courses` / `users` / `items`)
// alongside `total`, `page`, `page_size` siblings. UI consumers use these to
// render the <AdminPagination> footer.
export type AdminCourseListResponse = {
  courses: AdminCourseSummary[];
  total: number;
  page: number;
  page_size: number;
};

// Quick task 260512-fyp (v1_fixes #13) — admin Users list page.
// last_login_at is null until the user's first post-migration magic-link
// verification; the admin page renders an em-dash for null.
//
// Quick task 260518-pzy — magic_link_url is the fully-built URL for the
// latest still-active magic link issued to this user (null when no active
// link). Required (not optional) so the TS compiler flags any code path
// that forgets to thread it through. See the api/Kidology.Api PLAN §Risks
// for the security tradeoff persisting raw tokens introduces.
//
// Quick task 260519-rj6 — login_count is the running per-user sign-in
// counter, always present (server default 0); same required-not-optional
// rationale as magic_link_url — any builder that forgets to thread it must
// fail at compile time. Backfilled to 1 for legacy rows with a populated
// last_login_at (floor, not a true historical count — see the User entity
// XML doc + this quick task's SUMMARY Tradeoffs).
export type AdminUserDto = {
  id: string;
  email: string;
  created_at: string;            // ISO-8601
  last_login_at: string | null;  // ISO-8601 or null
  magic_link_url: string | null; // fully-built /auth/callback URL or null
  login_count: number;           // running per-user sign-in counter (>= 0)
  is_suspended: boolean;
};

export type AdminUserListResponse = {
  users: AdminUserDto[];
  total: number;
  page: number;
  page_size: number;
};

// Quick task 260520-uud — admin user detail page. Superset of AdminUserDto
// with is_admin (derived from ADMIN_EMAILS env allowlist) + course_progress
// (per-course PercentComplete pulled from CourseProgressStubs).
export type AdminUserCourseProgressDto = {
  course_id: string;
  course_title: string;
  percent_complete: number;
};

// Quick task 260520-wb1 — per-user sign-in IP row, surfaced on /admin/users/:id.
// Sorted last_seen_at DESC by the server projection. ip_address is the
// canonical string form (IPv4 dotted-quad or IPv6 RFC 5952).
export type AdminUserIpAddressDto = {
  ip_address: string;
  first_seen_at: string;          // ISO-8601
  last_seen_at: string;           // ISO-8601
  seen_count: number;
};

export type AdminUserDetailDto = AdminUserDto & {
  is_admin: boolean;
  course_progress: AdminUserCourseProgressDto[];
  ip_addresses: AdminUserIpAddressDto[];   // quick task 260520-wb1
  has_coach_subscription: boolean;
  funnel_source: string | null;
  coach_subscription_expires_at: string | null;
  name?: string;
};

// Quick task 260520-rqi — admin Add Student. Server creates a User row +
// issues a fresh magic-link token in the same transaction; response shape
// matches AdminUserDto so the page can splice (or invalidate-and-refetch)
// without an extra round-trip.
export type CreateAdminUserRequest = {
  email: string;
};

export type AdminEmailTemplateDto = {
  key: string;
  name: string;
  group: string;
  template_id: string;
  subject: string;
  requires_login_link: boolean;
  needs_pdf_report: boolean;
};

export type AdminEmailPdfReportDto = {
  key: string;
  name: string;
};

export type AdminEmailTemplateListResponse = {
  tenant: "kidology" | "mindfulme";
  templates: AdminEmailTemplateDto[];
  pdf_reports: AdminEmailPdfReportDto[];
};

export type SendAdminEmailRequest = {
  email: string;
  template_key: string;
  subject?: string;
  pdf_report?: string;
};

export type SendAdminEmailResponse = {
  sent: boolean;
  email: string;
  template_key: string;
  subject: string;
  magic_link_url: string | null;
  magic_link_issued: boolean;
};

// Quick task 260514-omd — shared paging params passed to listCourses /
// listUsers / (inline) listLibrary. Both fields optional; the server defaults
// page=1, page_size=10 and clamps page_size to [1, 100].
export type AdminPaginationParams = {
  page?: number;
  pageSize?: number;
  q?: string;
  hasActiveSub?: boolean;
  isSuspended?: boolean;
};

export type CreateCourseRequest = {
  title: string;
  subtitle: string;
  short_description: string;
  long_description: string;
  slug: string;
  spanish_title?: string;
  spanish_subtitle?: string;
  spanish_short_description?: string;
  spanish_long_description?: string;
  portuguese_title?: string;
  portuguese_subtitle?: string;
  portuguese_short_description?: string;
  portuguese_long_description?: string;
};

export type UpdateCourseRequest = Partial<
  Omit<AdminCourseDetail, "id" | "created_at" | "updated_at" | "status">
>;

/* ──────────────────────────────────────────────────────────────────────
 * Slice C / plan 03-03 — admin curriculum types + endpoints.
 *
 * Wire format mirrors the public catalog convention (snake_case_lower for
 * the LessonItem.type enum). `description` lands here in Slice C so the
 * activity-type LessonItemEditor body can persist content alongside title +
 * duration. `playback_status`, `playback_id`, `quiz_id` are nullable in
 * Slice C — Slice D (Mux) and Slice E (Quiz) fill them.
 * ────────────────────────────────────────────────────────────────────── */

// Quick task 260512-nbg (v1_fixes #7): "activity" renamed to "materials" —
// a Materials lesson item can carry an optional file attachment via
// materials_resource_id (LessonItemEditor uploads it).
// Quick task 260517-p4r: fifth wire type "audio".
export type AdminLessonItemType =
  | "lesson"
  | "quiz"
  | "bonus_lesson"
  | "materials"
  | "audio";

export type LessonItemDto = {
  id: string;
  position: number;
  type: AdminLessonItemType;
  title: string;
  duration_minutes: number | null;
  description: string | null;
  playback_status: string | null;
  playback_id: string | null;
  spanish_playback_status?: string | null;
  spanish_playback_id?: string | null;
  portuguese_playback_status?: string | null;
  portuguese_playback_id?: string | null;
  quiz_id: string | null;
  materials_resource_id: string | null;
  spanish_materials_resource_id?: string | null;
  portuguese_materials_resource_id?: string | null;
  // Quick task 260517-p4r: R2 key for audio-type lesson items. Null until
  // the admin completes the /audio/{presign,ack} flow.
  audio_s3_key: string | null;
  spanish_audio_s3_key?: string | null;
  portuguese_audio_s3_key?: string | null;
  spanish_title?: string | null;
  spanish_description?: string | null;
  portuguese_title?: string | null;
  portuguese_description?: string | null;
  // feat/admin-ai-text-format: saved AI formatting per language and whether it
  // was generated from the current description (false = stale).
  formatted_description?: unknown;
  formatted_description_is_current?: boolean;
  spanish_formatted_description?: unknown;
  spanish_formatted_description_is_current?: boolean;
  portuguese_formatted_description?: unknown;
  portuguese_formatted_description_is_current?: boolean;
};

export type DayDto = {
  id: string;
  position: number;
  title: string;
  summary: string;
  lesson_items: LessonItemDto[];
  spanish_title?: string | null;
  spanish_summary?: string | null;
  portuguese_title?: string | null;
  portuguese_summary?: string | null;
};

export type CurriculumResponse = { days: DayDto[] };

export type CreateDayRequest = {
  title: string;
  summary?: string;
  spanish_title?: string;
  spanish_summary?: string;
  portuguese_title?: string;
  portuguese_summary?: string;
};
export type UpdateDayRequest = {
  title?: string;
  summary?: string;
  spanish_title?: string;
  spanish_summary?: string;
  portuguese_title?: string;
  portuguese_summary?: string;
};
export type CreateLessonItemRequest = {
  type: AdminLessonItemType;
  title: string;
  duration_minutes?: number;
  description?: string;
  spanish_title?: string;
  spanish_description?: string;
  portuguese_title?: string;
  portuguese_description?: string;
};
export type UpdateLessonItemRequest = {
  title?: string;
  duration_minutes?: number;
  description?: string;
  spanish_title?: string;
  spanish_description?: string;
  portuguese_title?: string;
  portuguese_description?: string;
  // Quick task 260512-nbg (v1_fixes #7): attach a Resource to a Materials
  // lesson item. Set the id to attach; set `materials_resource_id_clear=true`
  // to detach. Omit both to leave the existing attachment untouched.
  materials_resource_id?: string;
  materials_resource_id_clear?: boolean;
  spanish_materials_resource_id?: string;
  spanish_materials_resource_id_clear?: boolean;
  portuguese_materials_resource_id?: string;
  portuguese_materials_resource_id_clear?: boolean;
  // Quick task 260517-p4r: AudioUploader PATCHes this after a successful
  // /audio/ack so the admin curriculum sidebar reflects the new attachment
  // without a second round-trip. Replace overwrites; no _clear flag in v1.
  audio_s3_key?: string;
  spanish_audio_s3_key?: string;
  portuguese_audio_s3_key?: string;
  // feat/admin-ai-text-format: formatted JSON accepted in the admin preview.
  // Omit to leave untouched; `*_clear: true` removes the formatting.
  formatted_description?: unknown;
  formatted_description_clear?: boolean;
  spanish_formatted_description?: unknown;
  spanish_formatted_description_clear?: boolean;
  portuguese_formatted_description?: unknown;
  portuguese_formatted_description_clear?: boolean;
};

/** POST /api/admin/lesson-items/format-preview — nothing is saved. */
export type FormatLessonTextResponse = {
  formatted: unknown;
  warnings: string[];
  fidelity_missing: string[];
  fidelity_added: string[];
};

/* ──────────────────────────────────────────────────────────────────────
 * Quick task 260517-p4r — admin audio upload types. Two-step presign+ack
 * flow mirroring the worksheet upload pattern.
 * ────────────────────────────────────────────────────────────────────── */
export type PresignLessonItemAudioRequest = {
  filename: string;
  content_type: string;
  size_bytes: number;
};

export type PresignLessonItemAudioResponse = {
  s3_key: string;
  upload_url: string;
};

export type AckLessonItemAudioRequest = {
  s3_key: string;
  content_type: string;
  size_bytes: number;
};

export type AckLessonItemAudioResponse = {
  s3_key: string;
};

/* ──────────────────────────────────────────────────────────────────────
 * Slice F / plan 03-06 — admin resources types.
 *
 * Two-step pre-signed flow surface:
 *   1. presignResourceUpload — server validates size + MIME, mints a
 *      15-min PUT URL with the caller's Content-Type signed in.
 *   2. Browser PUTs the bytes directly to S3 with the matching Content-Type
 *      (Pitfall 2 — must match the signed value).
 *   3. ackResource — server verifies the object exists, conditionally
 *      sniffs %PDF- magic bytes (only when mime_type=application/pdf),
 *      creates the row + audit row.
 *
 * Quick task 260512-nbg (v1_fixes #7): widened from PDF-only to any
 * well-formed MIME so Materials lesson items can attach any file type.
 * ────────────────────────────────────────────────────────────────────── */

export type ResourceDto = {
  id: string;
  filename: string;
  size_bytes: number;
  uploaded_at: string;
  mime_type: string;
};

export type ResourceListResponse = { resources: ResourceDto[] };

export type PresignResourceUploadRequest = {
  filename: string;
  size_bytes: number;
  mime_type: string;
};

export type PresignResourceUploadResponse = {
  resource_id: string;
  s3_key: string;
  upload_url: string;
  mime_type: string;
};

export type AckResourceRequest = {
  resource_id: string;
  s3_key: string;
  filename: string;
  size_bytes: number;
  mime_type: string;
};

/* ──────────────────────────────────────────────────────────────────────
 * Quick task 260510-uae — admin course hero image upload types.
 *
 * Mirrors the Slice F resource flow shape: presign → browser PUT → ack.
 * Only difference: hero image is 1-per-course (no resource_id segment),
 * and the accepted content types are JPG/PNG/WebP, not application/pdf.
 * ────────────────────────────────────────────────────────────────────── */

export type CourseHeroImagePresignRequest = {
  filename: string;
  content_type: "image/jpeg" | "image/png" | "image/webp";
  size_bytes: number;
  is_spanish?: boolean;
  is_portuguese?: boolean;
};

export type CourseHeroImagePresignResponse = {
  s3_key: string;
  upload_url: string;
};

export type CourseHeroImageAckRequest = {
  s3_key: string;
  content_type: "image/jpeg" | "image/png" | "image/webp";
  is_spanish?: boolean;
  is_portuguese?: boolean;
};

export type CourseHeroImageAckResponse = {
  hero_image_s3_key: string;
};

/* ──────────────────────────────────────────────────────────────────────
 * Slice G / plan 03-07 — admin dashboard types.
 *
 * /api/admin/dashboard/kpis returns the two live KPI counts: course_count
 * and total_user_count (users minus ADMIN_EMAILS allowlist members).
 *
 * /api/admin/audit/recent returns the last-N rows of audit_log with a
 * server-derived `link` field — `course` rows route to the course editor;
 * other entity types return null in v1.
 * ────────────────────────────────────────────────────────────────────── */

export type DashboardKpisResponse = {
  course_count: number;
  total_user_count: number;
};

export type AuditRow = {
  id: string;
  action: string;
  entity_type: string;
  entity_id: string;
  actor_email: string;
  created_at: string;
  link: string | null;
};

export type RecentActivityResponse = {
  rows: AuditRow[];
};

/* ──────────────────────────────────────────────────────────────────────
 * Slice E / plan 03-05 — admin quiz types.
 *
 * QuizEditor sends the full question / answer set on Save: the canonical
 * mutation is PUT-replace (replaceQuizQuestions), not per-question / per-
 * answer CRUD. is_correct lives on every AnswerInput; the DB partial unique
 * index uq_quiz_answers_one_correct enforces "exactly one correct per
 * question". quick-260518-vqw: pass_threshold removed — submission alone
 * marks the lesson item complete.
 * ────────────────────────────────────────────────────────────────────── */

export type AnswerDto = {
  id: string;
  position: number;
  text: string;
  is_correct: boolean;
  spanish_text?: string | null;
  portuguese_text?: string | null;
};

export type QuestionDto = {
  id: string;
  position: number;
  prompt: string;
  answers: AnswerDto[];
  spanish_prompt?: string | null;
  portuguese_prompt?: string | null;
};

export type QuizDto = {
  id: string;
  lesson_item_id: string;
  questions: QuestionDto[];
};

export type AnswerInput = {
  text: string;
  is_correct: boolean;
  spanish_text?: string;
  portuguese_text?: string;
};

export type QuestionInput = {
  prompt: string;
  answers: AnswerInput[];
  spanish_prompt?: string;
  portuguese_prompt?: string;
};

export type CreateQuizForLessonItemRequest = {
  questions: QuestionInput[];
};

export type ReplaceQuizQuestionsRequest = {
  questions: QuestionInput[];
};

/**
 * Quick task 260514-omd — build a `?page=&page_size=` query string from the
 * optional pagination params. Returns an empty string when both are unset so
 * callers can append unconditionally.
 */
function paginationQs(params?: AdminPaginationParams): string {
  if (!params) return "";
  const qs = new URLSearchParams();
  if (params.page !== undefined) qs.set("page", String(params.page));
  if (params.pageSize !== undefined) qs.set("page_size", String(params.pageSize));
  if (params.q !== undefined && params.q !== "") qs.set("q", params.q);
  if (params.hasActiveSub !== undefined) {
    qs.set("has_active_sub", String(params.hasActiveSub));
  }
  if (params.isSuspended !== undefined) {
    qs.set("is_suspended", String(params.isSuspended));
  }
  const s = qs.toString();
  return s ? `?${s}` : "";
}

export const adminApi = {
  listCourses: (params?: AdminPaginationParams) =>
    api<AdminCourseListResponse>(`/api/admin/courses${paginationQs(params)}`),
  // Quick task 260512-fyp (v1_fixes #13) — admin Users list page.
  // Quick task 260514-omd: accepts pagination params (server-side offset/limit).
  listUsers: (params?: AdminPaginationParams) =>
    api<AdminUserListResponse>(`/api/admin/users${paginationQs(params)}`),
  // Quick task 260520-uud — admin user detail page (/admin/users/:id).
  getUser: (id: string) => api<AdminUserDetailDto>(`/api/admin/users/${id}`),
  getUserByEmail: (email: string) => api<AdminUserDetailDto>(`/api/admin/users/by-email/${encodeURIComponent(email)}`),
  grantConsultation: (id: string, days?: number) =>
    api<{ hasCoachSubscription: boolean; funnelSource: string | null }>(
      `/api/admin/users/${id}/grant-consultation`,
      {
        method: "POST",
        body: days !== undefined ? JSON.stringify({ days }) : undefined,
      }
    ),
  toggleSuspend: (id: string) =>
    api<{ isSuspended: boolean }>(`/api/admin/users/${id}/toggle-suspend`, {
      method: "POST",
    }),
  // Quick task 260520-rqi — admin Add Student row. Returns the freshly-created
  // user (with magic_link_url populated so it's immediately copyable).
  createUser: (body: CreateAdminUserRequest) =>
    api<AdminUserDto>("/api/admin/users", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  listEmailTemplates: () =>
    api<AdminEmailTemplateListResponse>("/api/admin/emails/templates"),
  sendEmail: (body: SendAdminEmailRequest) =>
    api<SendAdminEmailResponse>("/api/admin/emails/send", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  getCourse: (id: string) => api<AdminCourseDetail>(`/api/admin/courses/${id}`),
  createCourse: (body: CreateCourseRequest) =>
    api<AdminCourseDetail>("/api/admin/courses", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateCourse: (id: string, body: UpdateCourseRequest) =>
    api<AdminCourseDetail>(`/api/admin/courses/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  publishCourse: (id: string) =>
    api<AdminCourseDetail>(`/api/admin/courses/${id}/publish`, {
      method: "POST",
      body: "{}",
    }),
  unpublishCourse: (id: string) =>
    api<AdminCourseDetail>(`/api/admin/courses/${id}/unpublish`, {
      method: "POST",
      body: "{}",
    }),
  deleteCourse: (id: string) =>
    api<void>(`/api/admin/courses/${id}`, { method: "DELETE" }),

  // Slice C / plan 03-03 — curriculum (Day + LessonItem CRUD + 2 reorder + nested read).
  getCurriculum: (courseId: string) =>
    api<CurriculumResponse>(`/api/admin/courses/${courseId}/curriculum`),
  createDay: (courseId: string, body: CreateDayRequest) =>
    api<DayDto>(`/api/admin/courses/${courseId}/days`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateDay: (dayId: string, body: UpdateDayRequest) =>
    api<DayDto>(`/api/admin/days/${dayId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteDay: (dayId: string) =>
    api<void>(`/api/admin/days/${dayId}`, { method: "DELETE" }),
  reorderDays: (courseId: string, orderedIds: string[]) =>
    api<void>(`/api/admin/courses/${courseId}/days/reorder`, {
      method: "PATCH",
      body: JSON.stringify({ ordered_ids: orderedIds }),
    }),
  createLessonItem: (dayId: string, body: CreateLessonItemRequest) =>
    api<LessonItemDto>(`/api/admin/days/${dayId}/lesson-items`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateLessonItem: (itemId: string, body: UpdateLessonItemRequest) =>
    api<LessonItemDto>(`/api/admin/lesson-items/${itemId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  formatLessonText: (text: string) =>
    api<FormatLessonTextResponse>(`/api/admin/lesson-items/format-preview`, {
      method: "POST",
      body: JSON.stringify({ text }),
    }),
  deleteLessonItem: (itemId: string) =>
    api<void>(`/api/admin/lesson-items/${itemId}`, { method: "DELETE" }),
  reorderLessonItems: (dayId: string, orderedIds: string[]) =>
    api<void>(`/api/admin/days/${dayId}/lesson-items/reorder`, {
      method: "PATCH",
      body: JSON.stringify({ ordered_ids: orderedIds }),
    }),

  // Quick task 260517-p4r — admin audio presign + ack for audio-type
  // LessonItems. Mirrors the worksheet flow (server-controlled S3 key,
  // 100MB cap, magic-byte sniff on ack for mp3/wav/ogg/webm).
  presignLessonItemAudio: (
    lessonItemId: string,
    body: PresignLessonItemAudioRequest,
    locale?: string,
  ) =>
    api<PresignLessonItemAudioResponse>(
      `/api/admin/lesson-items/${lessonItemId}/audio/presign${locale ? `?locale=${locale}` : ""}`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  ackLessonItemAudio: (
    lessonItemId: string,
    body: AckLessonItemAudioRequest,
    locale?: string,
  ) =>
    api<AckLessonItemAudioResponse>(
      `/api/admin/lesson-items/${lessonItemId}/audio/ack${locale ? `?locale=${locale}` : ""}`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  createVideoUpload: (lessonItemId: string, locale?: string) =>
    api<CreateVideoUploadResponse>(
      `/api/admin/lesson-items/${lessonItemId}/upload-url${locale ? `?locale=${locale}` : ""}`,
      { method: "POST", body: "{}" },
    ),
  // Polled by useLessonItemPlaybackStatus while playback_status ∈
  // {uploading, processing}. Stops on ready/error (UI-SPEC §13).
  getPlaybackStatus: (lessonItemId: string, locale?: string) =>
    api<PlaybackStatusResponse>(
      `/api/admin/lesson-items/${lessonItemId}/playback-status${locale ? `?locale=${locale}` : ""}`,
    ),
  // Quick task 260511-zxq — short-TTL signed playback URL for the admin
  // Preview modal. Mux assets use a signed playback policy in prod, so the
  // bare playback_id 403s when passed straight to <MuxPlayer>. Fetched
  // on-demand when the modal opens.
  getVideoPreviewUrl: (lessonItemId: string, locale?: string) =>
    api<VideoPreviewUrlResponse>(
      `/api/admin/lesson-items/${lessonItemId}/preview-url${locale ? `?locale=${locale}` : ""}`,
    ),

  // Slice F / plan 03-06 — Resource (PDF) upload + download + delete.
  listResources: (courseId: string) =>
    api<ResourceListResponse>(`/api/admin/courses/${courseId}/resources`),
  presignResourceUpload: (
    courseId: string,
    body: PresignResourceUploadRequest,
  ) =>
    api<PresignResourceUploadResponse>(
      `/api/admin/courses/${courseId}/resources/presigned-upload`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  ackResource: (courseId: string, body: AckResourceRequest) =>
    api<ResourceDto>(`/api/admin/courses/${courseId}/resources`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deleteResource: (courseId: string, resourceId: string) =>
    api<void>(
      `/api/admin/courses/${courseId}/resources/${resourceId}`,
      { method: "DELETE" },
    ),
  // Caller links to this URL via <a target="_blank">; the server 302s to a
  // 5-min presigned-GET URL so the file opens in the user's PDF viewer
  // without exposing the signed URL to JS.
  resourceDownloadUrl: (courseId: string, resourceId: string) =>
    `/api/admin/courses/${courseId}/resources/${resourceId}/download`,

  // Quick task 260510-uae — course hero image upload (presign + ack).
  presignCourseHeroImageUpload: (
    courseId: string,
    body: CourseHeroImagePresignRequest,
  ) =>
    api<CourseHeroImagePresignResponse>(
      `/api/admin/courses/${courseId}/hero-image/presign`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  ackCourseHeroImage: (courseId: string, body: CourseHeroImageAckRequest) =>
    api<CourseHeroImageAckResponse>(
      `/api/admin/courses/${courseId}/hero-image/ack`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  // Slice E / plan 03-05 — Quiz CRUD + PUT-replace for the question set.
  getQuiz: (quizId: string) =>
    api<QuizDto>(`/api/admin/quizzes/${quizId}`),
  createQuizForLessonItem: (
    lessonItemId: string,
    body: CreateQuizForLessonItemRequest,
  ) =>
    api<QuizDto>(`/api/admin/lesson-items/${lessonItemId}/quiz`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  replaceQuizQuestions: (quizId: string, questions: QuestionInput[]) =>
    api<QuizDto>(`/api/admin/quizzes/${quizId}/questions`, {
      method: "PUT",
      body: JSON.stringify({ questions } satisfies ReplaceQuizQuestionsRequest),
    }),
  deleteQuiz: (quizId: string) =>
    api<void>(`/api/admin/quizzes/${quizId}`, { method: "DELETE" }),

  // Slice G / plan 03-07 — admin dashboard read endpoints.
  getDashboardKpis: () =>
    api<DashboardKpisResponse>("/api/admin/dashboard/kpis"),
  getRecentAudit: (limit = 10) =>
    api<RecentActivityResponse>(`/api/admin/audit/recent?limit=${limit}`),
};

/* ──────────────────────────────────────────────────────────────────────
 * Slice D — admin video DTO shapes mirror api/Kidology.Api/Admin/Dtos/AdminVideoDtos.cs
 * ────────────────────────────────────────────────────────────────────── */

export type CreateVideoUploadResponse = {
  upload_id: string;
  url: string;
};

export type PlaybackStatus =
  | "idle"
  | "uploading"
  | "processing"
  | "ready"
  | "error";

export type PlaybackStatusResponse = {
  playback_status: string;
  playback_id: string | null;
  duration_minutes: number | null;
};

// Quick task 260511-zxq — signed preview URL response. Mirrors
// SignedPlaybackUrlDto on the learner side so the modal can pass
// playback_url + thumbnail_url straight to MuxPlayer's src/poster props.
export type VideoPreviewUrlResponse = {
  playback_url: string;
  thumbnail_url: string;
  expires_at: string;
};
